var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable } from "mobx";
export const ERROR_TIME_WINDOWS = [
    { label: "Last 24 hours", hours: 24 },
    { label: "Last 7 days", hours: 24 * 7 },
    { label: "Last 30 days", hours: 24 * 30 },
    { label: "Last 1 year", hours: 24 * 365 },
];
export class GlobalAdminDocumentRetrievalViewModel {
    constructor(container) {
        this.currentTab = "retrieval";
        this.errorsSinceHours = 24 * 7;
        this.patientIdInput = "";
        this.firstNameInput = "";
        this.lastNameInput = "";
        this.emailInput = "";
        this.errorMessage = null;
        this.successMessage = null;
        this.kbDetailsExpanded = false;
        this.s3DetailsExpanded = false;
        this.batchesExpanded = true;
        this.expandedBatchIds = new Set();
        this.batchLoadErrorsById = new Map();
        this.globalAdminRetrievalManager = container.globalAdminRetrievalManager;
        this.bannerUIManager = container.bannerUIManager;
    }
    get summary() {
        return this.globalAdminRetrievalManager.summary.value;
    }
    get patientDetails() {
        return this.globalAdminRetrievalManager.currentPatientDetails;
    }
    get isLoading() {
        return this.globalAdminRetrievalManager.loadingPatientDetails;
    }
    get isActionLoading() {
        return this.globalAdminRetrievalManager.actionLoading;
    }
    get canMigrate() {
        if (!this.patientDetails)
            return false;
        return this.patientDetails.status.needsMigration;
    }
    get searchResults() {
        return this.globalAdminRetrievalManager.searchResults;
    }
    get isSearchLoading() {
        return this.globalAdminRetrievalManager.searchLoading;
    }
    get hasSearchQuery() {
        return !!(this.firstNameInput.trim() || this.lastNameInput.trim() || this.emailInput.trim());
    }
    get patientsWithErrors() {
        return this.globalAdminRetrievalManager.patientsWithErrors;
    }
    get patientsWithErrorsLoading() {
        return this.globalAdminRetrievalManager.patientsWithErrorsLoading;
    }
    isPatientRetrying(patientId) {
        return this.globalAdminRetrievalManager.retryingPatientIds.has(patientId);
    }
    isBatchLoading(batchId) {
        return this.globalAdminRetrievalManager.isBatchLoading(batchId);
    }
    getBatchDetails(batchId) {
        return this.globalAdminRetrievalManager.getBatchDetails(batchId);
    }
    isBatchExpanded(batchId) {
        return this.expandedBatchIds.has(batchId);
    }
    getBatchLoadError(batchId) {
        var _a;
        return (_a = this.batchLoadErrorsById.get(batchId)) !== null && _a !== void 0 ? _a : null;
    }
    async loadBatchDetails(batchId, forceReload = false) {
        var _a;
        const patientId = (_a = this.patientDetails) === null || _a === void 0 ? void 0 : _a.status.patientId;
        if (!patientId)
            return null;
        return this.globalAdminRetrievalManager.loadBatchDetails(patientId, batchId, { forceReload });
    }
    toggleBatchExpanded(batchId) {
        const nextExpanded = new Set(this.expandedBatchIds);
        if (nextExpanded.has(batchId)) {
            nextExpanded.delete(batchId);
            this.expandedBatchIds = nextExpanded;
            return;
        }
        nextExpanded.add(batchId);
        this.expandedBatchIds = nextExpanded;
        void this.ensureBatchDetailsLoaded(batchId);
    }
    resetBatchExpansionState() {
        this.expandedBatchIds = new Set();
        this.batchLoadErrorsById = new Map();
    }
    clearBatchLoadError(batchId) {
        const next = new Map(this.batchLoadErrorsById);
        next.delete(batchId);
        this.batchLoadErrorsById = next;
    }
    setBatchLoadError(batchId, message) {
        const next = new Map(this.batchLoadErrorsById);
        next.set(batchId, message);
        this.batchLoadErrorsById = next;
    }
    async ensureBatchDetailsLoaded(batchId, forceReload = false) {
        if (!this.isBatchExpanded(batchId)) {
            return;
        }
        if (!forceReload && (!!this.getBatchDetails(batchId) || this.isBatchLoading(batchId))) {
            return;
        }
        this.clearBatchLoadError(batchId);
        const details = await this.loadBatchDetails(batchId, forceReload);
        if (!this.isBatchExpanded(batchId)) {
            return;
        }
        if (!details) {
            if (this.getBatchDetails(batchId)) {
                return;
            }
            this.setBatchLoadError(batchId, "Failed to load batch details");
        }
    }
    reloadExpandedBatchDetails() {
        for (const batchId of this.expandedBatchIds) {
            void this.ensureBatchDetailsLoaded(batchId, true);
        }
    }
    async loadPatientDetailsWithExpandedReload(patientId) {
        var _a;
        const previousPatientId = (_a = this.patientDetails) === null || _a === void 0 ? void 0 : _a.status.patientId;
        if (previousPatientId !== patientId) {
            this.resetBatchExpansionState();
        }
        const details = await this.globalAdminRetrievalManager.loadPatientDetails(patientId);
        if (details) {
            this.reloadExpandedBatchDetails();
        }
        return details;
    }
    setCurrentTab(tab) {
        this.currentTab = tab;
        if (tab === "errors") {
            void this.globalAdminRetrievalManager.loadPatientsWithVectorizationErrors(200, this.errorsSinceHours);
        }
    }
    setErrorsSinceHours(hours) {
        this.errorsSinceHours = hours;
        void this.globalAdminRetrievalManager.loadPatientsWithVectorizationErrors(200, hours);
    }
    setPatientIdInput(value) {
        this.patientIdInput = value;
        this.clearMessages();
    }
    setFirstNameInput(value) {
        this.firstNameInput = value;
    }
    setLastNameInput(value) {
        this.lastNameInput = value;
    }
    setEmailInput(value) {
        this.emailInput = value;
    }
    clearMessages() {
        this.errorMessage = null;
        this.successMessage = null;
    }
    clearSearchInputs() {
        this.firstNameInput = "";
        this.lastNameInput = "";
        this.emailInput = "";
        this.globalAdminRetrievalManager.clearSearchResults();
    }
    toggleKbDetails() {
        this.kbDetailsExpanded = !this.kbDetailsExpanded;
    }
    toggleS3Details() {
        this.s3DetailsExpanded = !this.s3DetailsExpanded;
    }
    toggleBatches() {
        this.batchesExpanded = !this.batchesExpanded;
    }
    async lookupPatient() {
        const patientId = this.patientIdInput.trim();
        if (!patientId) {
            this.errorMessage = "Please enter a patient ID";
            return;
        }
        this.clearMessages();
        const details = await this.loadPatientDetailsWithExpandedReload(patientId);
        if (!details) {
            this.errorMessage = "Patient not found or error fetching data";
        }
    }
    async searchPatients() {
        if (!this.hasSearchQuery) {
            this.errorMessage = "Please enter at least one search criteria";
            return;
        }
        this.clearMessages();
        const results = await this.globalAdminRetrievalManager.searchPatients({
            firstName: this.firstNameInput.trim() || undefined,
            lastName: this.lastNameInput.trim() || undefined,
            email: this.emailInput.trim() || undefined,
        });
        if (results.length === 0) {
            this.errorMessage = "No patients found matching the search criteria";
        }
    }
    async selectPatient(patient) {
        this.patientIdInput = patient.id;
        this.clearSearchInputs();
        this.clearMessages();
        const details = await this.loadPatientDetailsWithExpandedReload(patient.id);
        if (!details) {
            this.errorMessage = "Error loading patient details";
        }
    }
    async migratePatient() {
        if (!this.patientDetails)
            return;
        this.clearMessages();
        const result = await this.globalAdminRetrievalManager.migratePatient(this.patientDetails.status.patientId);
        if (result.success) {
            this.successMessage = result.message;
            this.bannerUIManager.displayBanner({ text: "Migration started successfully" });
            this.reloadExpandedBatchDetails();
        }
        else {
            this.errorMessage = result.message;
        }
    }
    refresh() {
        this.globalAdminRetrievalManager.refresh();
        if (this.patientDetails) {
            void this.loadPatientDetailsWithExpandedReload(this.patientDetails.status.patientId);
        }
    }
    async loadPatientsWithVectorizationErrors() {
        this.clearMessages();
        await this.globalAdminRetrievalManager.loadPatientsWithVectorizationErrors(200, this.errorsSinceHours);
    }
    async retryFailedEmbeddingChunks(patientId) {
        this.clearMessages();
        try {
            const result = await this.globalAdminRetrievalManager.retryFailedEmbeddingChunks(patientId);
            if (result.success) {
                this.bannerUIManager.displayBanner({ text: result.message });
            }
            else {
                this.errorMessage = result.message;
            }
        }
        catch (err) {
            this.errorMessage = `Failed to retry: ${err instanceof Error ? err.message : String(err)}`;
        }
    }
    clearCurrentPatient() {
        this.globalAdminRetrievalManager.clearCurrentPatient();
        this.patientIdInput = "";
        this.clearMessages();
        this.resetBatchExpansionState();
    }
    getMigrationStatusVariant(status) {
        switch (status) {
            case "COMPLETE":
                return "success";
            case "PENDING":
                return "warning";
            case "IN_PROGRESS":
                return "info";
            case "NOT_NEEDED":
                return "secondary";
            default:
                return "secondary";
        }
    }
    getVectorizationStatusVariant(status) {
        if (!status)
            return "secondary";
        switch (status.toUpperCase()) {
            case "COMPLETE":
            case "COMPLETED":
            case "SUCCESS":
                return "success";
            case "IN_PROGRESS":
            case "PENDING":
                return "warning";
            case "FAILED":
            case "ERROR":
                return "error";
            default:
                return "secondary";
        }
    }
    formatDate(date) {
        if (!date)
            return "-";
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    }
    formatDuration(start, end) {
        if (!start)
            return "-";
        if (!end) {
            const diffMs = Date.now() - start.getTime();
            return `${this.formatDurationMs(diffMs)} (in progress)`;
        }
        const diffMs = end.getTime() - start.getTime();
        return this.formatDurationMs(diffMs);
    }
    formatDurationMs(ms) {
        if (ms < 0)
            return "0:00:00";
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (n) => n.toString().padStart(2, "0");
        return `${hours}:${pad(minutes)}:${pad(seconds)}`;
    }
}
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "currentTab", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "errorsSinceHours", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "patientIdInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "firstNameInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "lastNameInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "emailInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "errorMessage", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "successMessage", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "kbDetailsExpanded", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "s3DetailsExpanded", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "batchesExpanded", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "expandedBatchIds", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "batchLoadErrorsById", void 0);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "summary", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "patientDetails", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "isLoading", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "isActionLoading", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "canMigrate", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "searchResults", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "isSearchLoading", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "hasSearchQuery", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "patientsWithErrors", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "patientsWithErrorsLoading", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "toggleBatchExpanded", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "resetBatchExpansionState", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "clearBatchLoadError", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setBatchLoadError", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setCurrentTab", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setErrorsSinceHours", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setPatientIdInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setFirstNameInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setLastNameInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setEmailInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "clearMessages", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "clearSearchInputs", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "toggleKbDetails", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "toggleS3Details", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "toggleBatches", null);
